import { Button, Form, Modal, Spin } from "antd";
import dayjs from "dayjs";
import { FC, useEffect } from "react";
import {
  CLIENT_KEYS,
  useCreateClient,
  useGetClient,
  useUpdateClient,
} from "~/services/client";
import { BOOKING_KEYS, useUpdateBooking } from "~/services/booking";
import { cn } from "~/utils";
import { ActiveTabClient, useClientStore } from "../clients.store";
import { LeftSidebar } from "./sidebar";
import { TabDocuments, TabInfo, TabPreferences, TabRates } from "./tabs";
import { useNotification } from "~/hooks";
import {
  ActiveStatus,
  PaymentTerms,
  PreferredCommunicationMethod,
  RateType,
} from "~/utils/constants";
import { useQueryClient } from "@tanstack/react-query";

type Props = {};

export const ClientModal: FC<Props> = () => {
  const { formState, closeClientModal } = useClientStore();

  const [form] = Form.useForm();

  const { data: client, isLoading } = useGetClient(formState.clientId);
  const mutationCreate = useCreateClient();
  const mutationUpdate = useUpdateClient();
  const mutationUpdateBooking = useUpdateBooking();
  const qc = useQueryClient();

  useEffect(() => {
    if (!form || !formState.open) return;

    if (formState.clientId && client) {
      form.setFieldsValue({
        ...client,
        preferredTimeWindowFrom: client.preferredTimeWindowFrom
          ? dayjs(client.preferredTimeWindowFrom)
          : null,
        preferredTimeWindowTo: client.preferredTimeWindowTo
          ? dayjs(client.preferredTimeWindowTo)
          : null,
      });
    } else {
      form.resetFields();

      // Parse prefill data from quick client
      const prefill = formState.prefillData;
      const nameParts = prefill?.name?.split(' ') || [];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      form.setFieldsValue({
        shippingAddresses: [{}],
        preferredCommunicationMethod: PreferredCommunicationMethod.EMAIL,
        paymenTerms: PaymentTerms.NET_30,
        rateType: RateType.STANDARD,
        status: ActiveStatus.ACTIVE,
        // Prefill from quick client data
        ...(prefill && {
          companyName: prefill.name,
          firstName,
          lastName,
          email: prefill.email,
          contactPhone: prefill.phone,
        }),
      });
    }
  }, [client, form, formState.clientId, formState.open, formState.prefillData]);

  const { success, error } = useNotification();

  const tabComponents = {
    [ActiveTabClient.INFO]: <TabInfo />,
    [ActiveTabClient.RATES]: <TabRates />,
    [ActiveTabClient.DOCUMENTS]: <TabDocuments />,
    [ActiveTabClient.PREFERENCES]: <TabPreferences />,
  };

  const flattenObject = (
    obj: any,
    result: Record<string, any> = {},
    prefix: string = ""
  ): Record<string, any> => {
    Object.entries(obj).forEach(([key, value]) => {
      const newKey = prefix ? `${prefix}[${key}]` : key;

      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (
            typeof item === "object" &&
            item !== null &&
            !(item instanceof File)
          ) {
            flattenObject(item, result, `${key}[${index}]`);
          } else if (item instanceof File) {
            result[`${key}[${index}]`] = item;
          } else if (item !== undefined && item !== null) {
            result[`${key}[${index}]`] = item;
          }
        });
      } else if (value instanceof File) {
        result[newKey] = value;
      } else if (dayjs.isDayjs(value) && value.isValid()) {
        result[newKey] = value.toISOString();
      } else if (typeof value === "object" && value !== null) {
        flattenObject(value, result, newKey);
      } else if (value !== undefined && value !== null) {
        result[newKey] = value;
      }
    });
    return result;
  };

  const convertToFormData = (data: any): FormData => {
    const formDataPayload = new FormData();
    const flattenedData = flattenObject(data);

    Object.entries(flattenedData).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => formDataPayload.append(key, item));
      } else {
        formDataPayload.append(key, value);
      }
    });

    return formDataPayload;
  };

  const handleSubmit = async () => {
    try {
      // Get form values to check createUserAccount
      const allValues = form.getFieldsValue(true);

      // Base fields to validate
      const fieldsToValidate = [
        "companyName",
        "firstName",
        "lastName",
      ];

      // Email required only if creating user account
      if (allValues.createUserAccount) {
        fieldsToValidate.push("email");
      }

      // Validate only the specified fields
      const data = await form.validateFields(fieldsToValidate);

      // Custom validation: require either email or phone
      if (!allValues.email && !allValues.contactPhone) {
        error({
          message: "Contact info required",
          description: "Please provide either an email or phone number.",
        });
        return;
      }

      // Get all form values, including non-validated ones
      const allData = form.getFieldsValue(true);
      const formDataPayload = convertToFormData({
        ...allData,
        ...data, // Ensure validated fields overwrite any defaults
      });

      // Only append password when creating a new client, not when updating
      // This prevents resetting the password to "password123" on every update
      if (!formState.clientId) {
        formDataPayload.append("password", "password123");
      }

      // for (const [key, value] of formDataPayload.entries()) {
      //   console.log(`${key}:`, value);
      // }

      if (formState.clientId) {
        await mutationUpdate.mutateAsync(
          {
            id: formState.clientId,
            formData: formDataPayload,
          },
          {
            onSuccess: () => {
              // Invalidate both client list and individual client cache
              qc.invalidateQueries({
                queryKey: CLIENT_KEYS.GET_CLIENTS({}),
              });
              qc.invalidateQueries({
                queryKey: CLIENT_KEYS.GET_CLIENT(formState.clientId),
              });
              success({
                message: "Client updated successfully",
                description: "The client has been updated successfully.",
              });
            },
            onError: () => {
              error({
                message: "Failed to update client",
                description: "An error occurred while updating the client.",
              });
            },
          }
        );
      } else {
        const newClient = await mutationCreate.mutateAsync(formDataPayload, {
          onError: (err) => {
            error({
              message: `Failed to create client ${err?.response.data.message}`,
              description: "An error occurred while creating the client.",
            });
          },
        });

        // If we have a booking to link, update it with the new client
        if (formState.bookingIdToLink && newClient?.id) {
          await mutationUpdateBooking.mutateAsync(
            {
              id: formState.bookingIdToLink,
              clientId: newClient.id,
              tempClientData: null, // Clear the temp client data
            },
            {
              onSuccess: () => {
                qc.invalidateQueries({
                  queryKey: BOOKING_KEYS.GET_BOOKING(formState.bookingIdToLink),
                });
                success({
                  message: "Client created and linked to booking",
                  description: "The quick client has been converted to a full client.",
                });
              },
              onError: () => {
                success({
                  message: "Client created successfully",
                  description: "Note: Failed to link to booking automatically.",
                });
              },
            }
          );
        } else {
          success({
            message: "Client created successfully",
            description: "The client has been created successfully.",
          });
        }
      }

      closeClientModal();
    } catch (err) {
      console.error("Validation failed:", err);
    }
  };

  return (
    <Modal
      destroyOnHidden
      open={formState.open}
      title={
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2">New Client Registration</h1>
          <p className="text-gray-600">
            Create a new client account with relevant details and preferences
          </p>
        </div>
      }
      footer={null}
      width={"70%"}
      onClose={() => closeClientModal()}
      onCancel={() => closeClientModal()}
    >
      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            shippingAddresses: [{}],
            preferredCommunicationMethod: PreferredCommunicationMethod.EMAIL,
            paymenTerms: PaymentTerms.NET_30,
            rateType: RateType.STANDARD,
            status: ActiveStatus.ACTIVE,
          }}
        >
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-3">
              <LeftSidebar />
            </div>
            <div className="col-span-9">
              <div className="bg-white rounded-lg shadow p-6">
                {Object.entries(tabComponents).map(([tabKey, component]) => (
                  <div
                    key={tabKey}
                    className={cn(
                      tabKey === formState.activeTab ? "block" : "hidden"
                    )}
                  >
                    {component}
                  </div>
                ))}

                <div className="mt-8 pt-6 border-t flex justify-end space-x-4">
                  <button
                    type="button"
                    className="px-4 py-2 border rounded-md hover:bg-gray-50"
                  >
                    Save as Draft
                  </button>
                  <Button
                    htmlType="button"
                    size="large"
                    type="primary"
                    onClick={handleSubmit}
                    disabled={
                      mutationCreate.isPending || mutationUpdate.isPending
                    }
                    loading={
                      mutationCreate.isPending || mutationUpdate.isPending
                    }
                  >
                    {formState.clientId ? "Update" : "Create"} Client
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};
